/*
  Ellarine - cart drawer (sections/el-cart-drawer.liquid).
  <el-cart-drawer id="cart-drawer"> replaces Horizon's cart drawer:
  - modal overlay at every width (50% backdrop, 200ms slide), closed by the X, a backdrop click or Escape;
  - quantity, remove and the gift box upsell post to /cart/update.js and /cart/add.js with this section
    bundled in the response (one round trip), then the drawer is redrawn with Horizon's morph;
  - follows every Shopify standard cart event fired elsewhere (product forms, quick add, apps) and
    opens itself after an add once the new content is drawn;
  - exposes window.EllarineCart { open, close, toggle, refresh, addItems } for the merchant bundle sections;
  - runs the reservation countdown: seconds left kept in sessionStorage, one tick per second on every page,
    back to the full time on every cart change, strip hidden at 0 (the cart is kept);
  - reads the cart again when it may have changed elsewhere (another tab, a restored page, a failed read)
    and says every change out loud through a polite live region inside the dialog.
  Prices and texts are never computed here: they all come from the Liquid render.
*/
import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents, createViewEventElement } from '@shopify/events';

const SOURCE = 'el-cart-drawer';
const TIMER_KEY = 'el-cart-timer';
const SLIDE_MS = 240;
const ERROR_MS = 6000;
/* Hidden longer than this (other tab, locked phone): the cart is read again before it is shown */
const STALE_AFTER_MS = 5000;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Thumbnails whose file failed once: the next redraw goes straight to the product image */
const failedImages = new Set();

function rootUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

/* sessionStorage throws in some private modes: the countdown then simply restarts on each page */
function readStorage(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    /* ignore */
  }
}

/** POST to the AJAX cart; Shopify errors (422 etc.) become a thrown Error with Shopify's own text. */
async function postCart(path, body) {
  const response = await fetch(`${rootUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    data = null;
  }
  if (!response.ok || !data || data.status) {
    const error = new Error(data?.description || data?.message || `Cart request failed (${response.status})`);
    error.status = Number(data?.status) || response.status;
    error.description = typeof data?.description === 'string' ? data.description : '';
    throw error;
  }
  return data;
}

async function readCart() {
  const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
  return response.json();
}

function cartEventResult(cart, detail) {
  return {
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, ...detail },
  };
}

/* Morph keys: cart lines by line key so a removed line never repaints the ones after it */
function nodeKey(node) {
  if (!(node instanceof Element)) return undefined;
  return node.getAttribute('data-key') || node.id || undefined;
}

/* Runs before morph copies the server attributes onto a live node */
function keepRuntimeState(oldNode, newNode) {
  MORPH_OPTIONS.onBeforeUpdate?.(oldNode, newNode);

  // A message shown under a line or the gift box stays while it is on screen (the server sends it empty)
  if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement && oldNode.matches('[data-el-line-error], [data-el-upsell-error]')) {
    if (!oldNode.hidden && oldNode.textContent) {
      newNode.hidden = false;
      newNode.textContent = oldNode.textContent;
    }
    return;
  }

  if (!(oldNode instanceof HTMLImageElement) || !(newNode instanceof HTMLImageElement)) return;

  const serverSrc = newNode.getAttribute('src');
  if (serverSrc && failedImages.has(serverSrc) && newNode.dataset.fallbackSrc) {
    newNode.setAttribute('src', newNode.dataset.fallbackSrc);
    newNode.removeAttribute('data-fallback-src');
    newNode.classList.remove('el-cart-line__image--contain');
    return;
  }

  // A cart image module (Cordelia / Family Ring) composed this thumbnail: keep it while the line is the same
  const composed = oldNode.hasAttribute('data-cordelia-image') || oldNode.hasAttribute('data-bs-image');
  if (composed && oldNode.dataset.elSrc === newNode.dataset.elSrc && oldNode.getAttribute('src') !== serverSrc) {
    for (const name of ['src', 'style', 'data-cordelia-image', 'data-bs-image']) {
      const value = oldNode.getAttribute(name);
      if (value === null) newNode.removeAttribute(name);
      else newNode.setAttribute(name, value);
    }
  }
}

const MORPH_CONFIG = {
  ...MORPH_OPTIONS,
  childrenOnly: false,
  getNodeKey: nodeKey,
  onBeforeUpdate: keepRuntimeState,
};

/* Fires Shopify's cart:view event (analytics) each time the dialog opens */
class ElCartView extends createViewEventElement(HTMLElement) {}

if (!customElements.get('el-cart-view')) customElements.define('el-cart-view', ElCartView);

/**
 * Must extend Horizon's Component: the header button calls it with on:click="#cart-drawer/toggle".
 */
class ElCartDrawer extends Component {
  #abort = null;
  #busy = false;
  #closeTimer = 0;
  #tickTimer = 0;
  #refreshAbort = null;
  #backdropPress = false;
  /* The drawn cart may be out of date (a background read failed, the tab was hidden): read it on the next open */
  #stale = false;
  #hiddenAt = 0;
  #liveTimer = 0;

  get sectionId() {
    return this.dataset.sectionId || 'el-cart-drawer';
  }

  get dialog() {
    return this.querySelector('dialog');
  }

  get isOpen() {
    const dialog = this.dialog;
    return Boolean(dialog?.open) && !dialog.classList.contains('is-closing');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const dialog = this.dialog;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('error', this.#onImageError, { signal, capture: true });
    dialog?.addEventListener('cancel', this.#onCancel, { signal });
    dialog?.addEventListener('close', this.#onDialogClose, { signal });
    dialog?.addEventListener('pointerdown', (event) => (this.#backdropPress = event.target === dialog), { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartEvent, { signal });
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartEvent, { signal });
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#onCartEvent, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal, capture: true });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    window.addEventListener('pagehide', this.#onPageHide, { signal });
    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });
    for (const name of ['shopify:section:load', 'shopify:section:select', 'shopify:section:deselect', 'shopify:block:select']) {
      document.addEventListener(name, this.#onEditorEvent, { signal });
    }

    this.#startTimer();
    this.#repairImages();

    if (this.hasAttribute('data-open-on-load')) requestAnimationFrame(() => this.open());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#refreshAbort?.abort();
    clearInterval(this.#tickTimer);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#liveTimer);
    if (this.dialog) unlockScroll(this.dialog);
  }

  /* ---------- open / close ---------- */

  /**
   * Opens the drawer. When the last read failed (loading error shown) it is tried again; when the drawn
   * cart may be out of date (restored page, tab hidden for a while) it is read again in the background.
   */
  open() {
    const wasOpen = Boolean(this.dialog?.open);
    this.#show();
    // a change in progress redraws the drawer with its own answer
    if (wasOpen || !this.dialog?.open || this.#busy) return;
    if (this.classList.contains('is-error')) this.refresh();
    else if (this.#stale) this.refresh({ quiet: true });
  }

  #show() {
    const dialog = this.dialog;
    if (!dialog) return;
    clearTimeout(this.#closeTimer);

    if (dialog.open) {
      dialog.classList.remove('is-closing');
      dialog.classList.add('is-open');
      return;
    }

    dialog.classList.remove('is-open', 'is-closing');
    dialog.showModal();
    // showModal() focuses the close button and may scroll the dialog box to reach it (browsers without overflow: clip)
    dialog.scrollLeft = 0;
    dialog.scrollTop = 0;
    lockScroll(dialog);
    this.setAttribute('open', '');
    this.#syncTriggers(true);
    this.#paintTimer();

    // Focus the close button (no page scroll), start from the closed position, then slide in
    dialog.querySelector('[data-el-cart-close]')?.focus({ preventScroll: true });
    if (!reducedMotion.matches) void dialog.querySelector('.el-cart__panel')?.offsetWidth;
    dialog.classList.add('is-open');
  }

  close() {
    const dialog = this.dialog;
    if (!dialog?.open || dialog.classList.contains('is-closing')) return;
    dialog.classList.add('is-closing');
    dialog.classList.remove('is-open');
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => dialog.close(), reducedMotion.matches ? 0 : SLIDE_MS);
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  /* The browser puts focus back on the element used to open the drawer, without scrolling the page */
  #onDialogClose = () => {
    const dialog = this.dialog;
    clearTimeout(this.#closeTimer);
    dialog?.classList.remove('is-open', 'is-closing');
    if (dialog) unlockScroll(dialog);
    this.removeAttribute('open');
    this.#syncTriggers(false);
    clearTimeout(this.#liveTimer);
    const region = this.querySelector('[data-el-cart-live]');
    if (region) region.textContent = '';
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #syncTriggers(open) {
    for (const trigger of document.querySelectorAll('[aria-controls="cart-drawer"]')) {
      trigger.setAttribute('aria-expanded', String(open));
    }
  }

  /* ---------- clicks, quantity input ---------- */

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target === this.dialog) {
      if (this.#backdropPress) this.close();
      return;
    }

    if (target.closest('[data-el-cart-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const step = target.closest('[data-el-qty]');
    if (step instanceof HTMLButtonElement) {
      const line = step.closest('[data-key]');
      const input = line?.querySelector('[data-el-qty-input]');
      if (!line || !(input instanceof HTMLInputElement)) return;
      const next = Math.max(0, Number(input.dataset.quantity) + Number(step.dataset.elQty));
      this.changeQuantity(line.getAttribute('data-key'), next, step);
      return;
    }

    const remove = target.closest('[data-el-remove]');
    if (remove) {
      this.changeQuantity(remove.closest('[data-key]')?.getAttribute('data-key'), 0, remove);
      return;
    }

    const upsell = target.closest('[data-el-cart-upsell]');
    if (upsell instanceof HTMLButtonElement) {
      const id = Number(upsell.dataset.elCartUpsell);
      if (!id || this.#busy) return;
      this.addItems([{ id, quantity: 1 }], { open: false, fromDrawer: true }).catch((error) => {
        const message = this.querySelector('[data-el-upsell-error]');
        if (message) this.#flash(message, this.#errorText(error));
      });
      return;
    }

    if (target.closest('[data-el-cart-checkout]') && this.#busy) event.preventDefault();
  };

  /* Typed quantities are sent when the field is left or when Enter is pressed; empty means remove */
  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-el-qty-input]')) {
      this.#commitInput(event.target);
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-el-qty-input]')) return;
    event.preventDefault();
    this.#commitInput(event.target);
  };

  #commitInput(input) {
    const line = input.closest('[data-key]');
    const current = Number(input.dataset.quantity);
    const raw = input.value.trim();
    const next = raw === '' ? 0 : Math.floor(Number(raw));
    if (!line || this.#busy || !Number.isFinite(next) || next < 0 || next === current) {
      if (!this.#busy) input.value = String(current);
      return;
    }
    this.changeQuantity(line.getAttribute('data-key'), next, null);
  }

  /* ---------- cart requests ---------- */

  /* A refused change rejects the event promise (as Horizon does); listeners handle it, never an unhandled rejection */
  #dispatchCartEvent(event) {
    event.promise?.catch(() => {});
    this.dispatchEvent(event);
  }

  /** Section ids to render with each request: this drawer plus Horizon's /cart page list when present. */
  #sectionsToRender() {
    const ids = new Set([this.sectionId]);
    for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }
    return Array.from(ids).slice(0, 5).join(',');
  }

  async changeQuantity(key, quantity, control) {
    if (!key || this.#busy) return;
    const title = this.#findLine(key)?.querySelector('.el-cart-line__title')?.textContent.trim() || '';
    this.#refreshAbort?.abort();
    this.#setBusy(true, control);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.#dispatchCartEvent(
      new CartLinesUpdateEvent({
        action: quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const cart = await postCart('cart/update.js', {
        updates: { [key]: quantity },
        sections: this.#sectionsToRender(),
        sections_url: window.location.pathname,
      });
      this.#resetTimer();
      await this.#draw(cart.sections?.[this.sectionId]);
      deferred.resolve(cartEventResult(cart, { sections: cart.sections ?? undefined, didError: false }));
      const live = this.#liveText(quantity > 0 ? 'liveUpdated' : 'liveRemoved', { title, quantity });
      this.#announce(`${live} ${this.#liveText('liveCount')}`);
    } catch (error) {
      deferred.reject(error);
      const text = this.#errorText(error);
      this.#showLineError(key, text);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error.message,
          code: error.status === 422 ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        })
      );
      // The drawn cart may be out of date (changed in another tab, a line that no longer exists):
      // read the real one; the message stays under the line, or is read out if the line is gone
      await this.refresh({ quiet: true });
      if (!this.#findLine(key)) this.#announce(text);
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Adds lines ({ id, quantity, properties }) in one request, redraws, then opens the drawer
   * (open: true / false; left out, the section setting "Open when a product is added" decides).
   * Resolves with the /cart/add.js answer, rejects with Shopify's message (for the caller to show).
   */
  async addItems(items, { open, fromDrawer = false } = {}) {
    const shouldOpen = open ?? this.autoOpen;
    const list = (Array.isArray(items) ? items : [items])
      .filter((item) => item && item.id)
      .map((item) => ({ ...item, quantity: Number(item.quantity) || 1 }));
    if (!list.length) throw new Error('No items to add');

    this.#refreshAbort?.abort();
    this.#setBusy(true, null);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.#dispatchCartEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: fromDrawer ? 'cart' : 'product',
        lines: list.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    let added;
    try {
      added = await postCart('cart/add.js', {
        items: list,
        sections: this.#sectionsToRender(),
        sections_url: window.location.pathname,
      });
    } catch (error) {
      this.#setBusy(false);
      readCart().then((cart) => deferred.resolve(cartEventResult(cart, { didError: true })), deferred.reject);
      this.dispatchEvent(
        new CartErrorEvent({ error: error.message, code: 'INVALID', detail: { description: error.description } })
      );
      throw error;
    }

    this.#resetTimer();
    await this.#draw(added.sections?.[this.sectionId]);
    this.#setBusy(false);
    if (shouldOpen) this.open();
    this.#announce(`${this.#liveText('liveAdded')} ${this.#liveText('liveCount')}`);

    // /cart/add.js does not return the whole cart: read it for the header count, after the drawer is drawn
    readCart().then(
      (cart) => deferred.resolve(cartEventResult(cart, { sections: added.sections ?? undefined, didError: false })),
      deferred.reject
    );
    return added;
  }

  /** "Open when a product is added" (section setting) */
  get autoOpen() {
    return this.dataset.autoOpen === 'true';
  }

  /**
   * Re-reads this section (Section Rendering API) and redraws.
   * open: open afterwards; reset: the cart changed elsewhere, restart the countdown;
   * quiet: the cart may not have changed (restored page, tab shown again), so a failed read keeps the
   * cart already drawn (read again on the next open) instead of showing the loading error.
   * A failed read that does show the loading error is tried again on the next open.
   */
  async refresh({ open = false, reset = false, quiet = false } = {}) {
    this.#refreshAbort?.abort();
    const controller = new AbortController();
    this.#refreshAbort = controller;
    try {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', this.sectionId);
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
      if (!response.ok) throw new Error(`Section request failed (${response.status})`);
      const sections = await response.json();
      const html = sections?.[this.sectionId];
      if (!html) throw new Error('Empty section');
      if (reset) this.#resetTimer();
      this.#render(html);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        if (quiet && !this.classList.contains('is-error')) this.#stale = true;
        else this.classList.add('is-error');
      }
    }
    if (open) this.#show();
  }

  async #draw(html, options) {
    if (html) this.#render(html);
    else await this.refresh(options);
  }

  #render(html) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-el-cart-inner]');
    const current = this.querySelector('[data-el-cart-inner]');
    if (!next || !current) return;
    const dialog = this.dialog;
    const hadFocus = Boolean(dialog?.contains(document.activeElement));

    try {
      morph(current, next, MORPH_CONFIG);
    } catch (error) {
      current.replaceWith(document.importNode(next, true));
    }

    this.classList.remove('is-error');
    this.#stale = false;
    this.#paintTimer();
    this.#repairImages();

    // The focused control was removed (last line, remove button): keep focus inside the drawer
    if (hadFocus && dialog?.open && !dialog.contains(document.activeElement)) {
      dialog.querySelector('[data-el-cart-close]')?.focus({ preventScroll: true });
    }
  }

  #setBusy(busy, control) {
    this.#busy = busy;
    this.classList.toggle('is-busy', busy);
    this.querySelector('[data-el-cart-body]')?.setAttribute('aria-busy', String(busy));
    for (const element of this.querySelectorAll('.is-loading')) element.classList.remove('is-loading');
    if (busy && control instanceof HTMLElement) control.classList.add('is-loading');
  }

  #findLine(key) {
    return Array.from(this.querySelectorAll('[data-key]')).find((element) => element.getAttribute('data-key') === key) || null;
  }

  /**
   * Text for a refused or failed change: Shopify's own reason for a real quantity or stock refusal (422);
   * a line key that is no longer in the cart ("updates parameter is invalid."), a network error or a
   * busy store (429, 5xx) get the general "could not be updated" message.
   */
  #errorText(error) {
    const description = typeof error?.description === 'string' ? error.description.trim() : '';
    if (error?.status === 422 && !/parameter/i.test(description)) return description || this.dataset.qtyError || '';
    return this.dataset.updateError || this.dataset.qtyError || '';
  }

  /* A refused quantity keeps the old values and says why under the line */
  #showLineError(key, text) {
    const line = this.#findLine(key);
    if (!line) return;
    const input = line.querySelector('[data-el-qty-input]');
    if (input instanceof HTMLInputElement) input.value = input.dataset.quantity || input.value;
    const message = line.querySelector('[data-el-line-error]');
    if (message && text) this.#flash(message, text);
  }

  #flash(element, text) {
    element.textContent = text;
    element.hidden = false;
    setTimeout(() => {
      if (element.textContent === text) element.hidden = true;
    }, ERROR_MS);
  }

  /* Live region texts (data-live-* on [data-el-cart-live]); [title], [quantity] and [count] are filled in */
  #liveText(name, { title = '', quantity = '' } = {}) {
    const region = this.querySelector('[data-el-cart-live]');
    const count = this.querySelector('[data-el-cart-inner]')?.getAttribute('data-count') ?? '';
    const template = region instanceof HTMLElement ? region.dataset[name] || '' : '';
    return template
      .replace('[title]', () => title)
      .replace('[quantity]', () => String(quantity))
      .replace('[count]', () => count)
      .trim();
  }

  /* Polite announcement inside the dialog (the header's status region is inert while the dialog is modal) */
  #announce(text) {
    const region = this.querySelector('[data-el-cart-live]');
    if (!(region instanceof HTMLElement) || !text.trim() || !this.dialog?.open) return;
    clearTimeout(this.#liveTimer);
    region.textContent = '';
    // Emptied first and filled a moment later, so the same message twice is read twice
    this.#liveTimer = setTimeout(() => {
      region.textContent = text.trim();
    }, 100);
  }

  /* ---------- events from the rest of the page ---------- */

  /* Product forms, quick add, Shopify.actions and the /cart page dispatch these; ours are skipped */
  #onCartEvent = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    const isLinesUpdate = event.type === StandardEvents.cartLinesUpdate;
    const autoOpen = isLinesUpdate && event.action === 'add' && this.dataset.autoOpen === 'true';
    let sourceModal = null;
    try {
      sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    } catch (error) {
      sourceModal = null;
    }

    if (!event.promise) {
      this.refresh();
      return;
    }

    event.promise
      .then(async (result) => {
        const detail = result?.detail;
        if (detail?.didError) return;
        if (isLinesUpdate) this.#resetTimer();
        await this.#draw(detail?.sections?.[this.sectionId]);
        if (!autoOpen) return;
        // Quick add: open once its own modal has closed, so focus returns to the right place
        if (sourceModal?.open) sourceModal.addEventListener('close', () => this.open(), { once: true });
        else this.open();
      })
      .catch(() => {});
  };

  /* On the /cart page the header icon is a plain link: open the drawer instead of reloading */
  #onDocumentClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('header-actions a.action__cart, a[data-el-cart-open]') : null;
    if (!link) return;
    event.preventDefault();
    this.open();
  };

  /* Back / forward: the page can come back from a cache with an old cart */
  #onPageShow = (event) => {
    const navigation = window.performance?.getEntriesByType?.('navigation')[0];
    if (event.persisted || navigation?.type === 'back_forward') this.refresh({ quiet: true });
  };

  /* Back on this tab after a while: the cart may have changed in another tab */
  #onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.#hiddenAt = Date.now();
      return;
    }
    if (!this.#hiddenAt || Date.now() - this.#hiddenAt < STALE_AFTER_MS) return;
    this.#hiddenAt = 0;
    if (this.dialog?.open && !this.#busy) this.refresh({ quiet: true });
    else this.#stale = true;
  };

  /* Never come back to a page with the drawer still open */
  #onPageHide = () => {
    if (this.dialog?.open) this.dialog.close();
  };

  /* Theme editor: open while the section or one of its blocks is selected */
  #onEditorEvent = (event) => {
    if (event.detail?.sectionId !== this.sectionId) return;
    if (event.type === 'shopify:section:deselect') this.close();
    else this.open();
  };

  /* ---------- thumbnails ---------- */

  #onImageError = (event) => {
    if (event.target instanceof HTMLImageElement) this.#useFallback(event.target);
  };

  #useFallback(image) {
    const fallback = image.dataset.fallbackSrc;
    if (!fallback) return;
    failedImages.add(image.getAttribute('src') || '');
    image.removeAttribute('data-fallback-src');
    image.classList.remove('el-cart-line__image--contain');
    image.setAttribute('src', fallback);
  }

  /* Images that failed before this script loaded never fire an error event for it */
  #repairImages() {
    for (const image of this.querySelectorAll('img[data-fallback-src]')) {
      if (image instanceof HTMLImageElement && image.complete && image.naturalWidth === 0) this.#useFallback(image);
    }
  }

  /* ---------- reservation countdown ---------- */

  get #timerFull() {
    return Math.max(1, Number(this.dataset.timerSeconds) || 600);
  }

  #timerLeft() {
    const stored = parseInt(readStorage(TIMER_KEY) ?? '', 10);
    return Number.isFinite(stored) ? Math.max(0, Math.min(stored, this.#timerFull)) : this.#timerFull;
  }

  #startTimer() {
    clearInterval(this.#tickTimer);
    if (this.dataset.timer !== 'true') return;
    this.#paintTimer();
    this.#tickTimer = setInterval(() => {
      const count = Number(this.querySelector('[data-el-cart-inner]')?.getAttribute('data-count'));
      if (!count) return;
      const left = this.#timerLeft();
      if (left <= 0) return;
      writeStorage(TIMER_KEY, String(left - 1));
      this.#paintTimer();
    }, 1000);
  }

  #resetTimer() {
    writeStorage(TIMER_KEY, String(this.#timerFull));
    this.#paintTimer();
  }

  #paintTimer() {
    const strip = this.querySelector('[data-el-cart-timer]');
    if (!(strip instanceof HTMLElement)) return;
    const left = this.#timerLeft();
    strip.hidden = left <= 0;
    const value = strip.querySelector('[data-el-timer-value]');
    const text = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
    if (value && value.textContent !== text) value.textContent = text;
  }
}

if (!customElements.get('el-cart-drawer')) customElements.define('el-cart-drawer', ElCartDrawer);

/* Shopify.actions.openCart (apps, Shopify features): open this drawer. Horizon's override in
   standard-actions-override.js looks for a theme-drawer and would send shoppers to /cart. */
function configureShopifyActions() {
  const run = () => {
    const openCart = window.Shopify?.actions?.openCart;
    if (!openCart || typeof openCart.configure !== 'function') return;
    openCart.configure({
      async handler() {
        const drawer = document.querySelector('el-cart-drawer');
        if (drawer instanceof ElCartDrawer) await drawer.refresh({ open: true });
        else window.location.href = `${rootUrl()}cart`;
      },
    });
  };
  run();
  // Horizon configures on DOMContentLoaded when the actions bundle is late: run again after it
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  window.addEventListener('load', run, { once: true });
}

configureShopifyActions();

/* Public API for the merchant sections (bundle-2-bague, bundle, bundle-pierres) and any other script */
function drawer() {
  const element = document.querySelector('el-cart-drawer');
  if (!(element instanceof ElCartDrawer)) throw new Error('el-cart-drawer is missing');
  return element;
}

window.EllarineCart = {
  open: () => drawer().open(),
  close: () => drawer().close(),
  toggle: () => drawer().toggle(),
  refresh: (options) => drawer().refresh(options),
  addItems: (items, options) => drawer().addItems(items, options),
};
